import { useCallback, useEffect, useState } from "react";
import { Loader2, Lock, Plus, Wallet } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { refOf, rowClick, useRowDetail } from "@/components/common/DetailSheet";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate } from "@/lib/format";

const TYPE_STYLE = {
  unrestricted: "border-primary/40 bg-primary/10 text-primary",
  restricted: "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300",
  designated: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  endowment: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  grant: "border-pink-800 bg-pink-950/60 text-pink-300",
  project: "border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300",
};

const BLANK = {
  code: "", name: "", fund_type: "restricted", currency: "INR", restriction_purpose: "",
  permitted_expenditure: [], geography_scope: "", fund_source: "domestic", opening_balance: 0,
};

export default function Funds() {
  const { can } = useAuth();
  const [funds, setFunds] = useState(null);
  const detail = useRowDetail("fund", () => load());
  const [form, setForm] = useState(BLANK);
  const [permitted, setPermitted] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/charity/funds").then((r) => setFunds(r.data)), []);
  useEffect(() => { load().catch(() => setFunds([])); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/charity/funds", {
        ...form,
        permitted_expenditure: permitted.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Fund created with its restriction definition");
      setOpen(false);
      setForm(BLANK);
      setPermitted("");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  if (!funds) return <Loading label="Loading funds" />;
  const total = funds.reduce((a, f) => a + (f.balance || 0), 0);
  const restricted = funds.filter((f) => f.fund_type !== "unrestricted").reduce((a, f) => a + (f.balance || 0), 0);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-007 · Fund Workspace"
        title="Restricted-fund integrity by construction"
        description="Fund types, restriction definitions and permitted expenditure lists. The engine blocks any posting that would breach a restriction or overspend a balance."
        actions={can("fund", "write") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-fund-button" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                <Plus className="mr-1.5 h-4 w-4" /> New fund
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg border-border bg-card">
              <DialogHeader><DialogTitle>Create a fund</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <F label="Code">
                  <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                         data-testid="fund-code-input" className="bg-muted/40 font-mono" />
                </F>
                <F label="Fund type">
                  <Select value={form.fund_type} onValueChange={(v) => setForm({ ...form, fund_type: v })}>
                    <SelectTrigger data-testid="fund-type-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.keys(TYPE_STYLE).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Name" full>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                         data-testid="fund-name-input" className="bg-muted/40" />
                </F>
                <F label="Restriction purpose" full>
                  <Input value={form.restriction_purpose}
                         onChange={(e) => setForm({ ...form, restriction_purpose: e.target.value })}
                         data-testid="fund-purpose-input" className="bg-muted/40" />
                </F>
                <F label="Permitted expenditure codes (comma separated)" full>
                  <Input value={permitted} onChange={(e) => setPermitted(e.target.value)}
                         data-testid="fund-permitted-input" className="bg-muted/40 font-mono text-xs" />
                </F>
                <F label="Fund source">
                  <Select value={form.fund_source} onValueChange={(v) => setForm({ ...form, fund_source: v })}>
                    <SelectTrigger data-testid="fund-source-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["domestic", "foreign", "mixed"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Opening balance">
                  <Input type="number" value={form.opening_balance}
                         onChange={(e) => setForm({ ...form, opening_balance: Number(e.target.value) })}
                         data-testid="fund-opening-input" className="bg-muted/40 font-mono" />
                </F>
              </div>
              <Button onClick={save} disabled={busy || !form.code || !form.name} data-testid="fund-save-button"
                      className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create fund
              </Button>
            </DialogContent>
          </Dialog>
        )}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Kpi testId="kpi-fund-total" label="Total balances" value={money(total, "INR", true)} accent="sky" icon={Wallet} />
        <Kpi testId="kpi-fund-restricted" label="Restricted / encumbered" value={money(restricted, "INR", true)}
             accent="violet" icon={Lock} sub={`${funds.filter((f) => f.fund_type !== "unrestricted").length} funds`} />
        <Kpi testId="kpi-fund-free" label="Free reserves" value={money(total - restricted, "INR", true)} accent="emerald" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
        {funds.map((f) => (
          <div key={f.id} className="panel relative overflow-hidden p-4" data-testid={`fund-card-${f.code}`}>
            <span className="absolute inset-x-0 top-0 h-[2px] bg-primary/70" />
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-mono text-xs text-primary">{f.code}</div>
                <div className="mt-1 text-sm font-semibold text-foreground">{f.name}</div>
              </div>
              <Chip className={TYPE_STYLE[f.fund_type]} testId={`fund-type-${f.code}`}>{f.fund_type}</Chip>
            </div>
            <div className="mt-3 font-mono text-xl font-semibold text-foreground">{money(f.balance, f.currency)}</div>
            {f.restriction_purpose && (
              <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">{f.restriction_purpose}</p>
            )}
            <div className="mt-3 flex flex-wrap gap-1">
              {(f.permitted_expenditure || []).map((p) => (
                <span key={p} className="rounded bg-muted px-1.5 py-0.5 font-mono text-[9px] text-muted-foreground">{p}</span>
              ))}
              {(f.permitted_expenditure || []).length === 0 && (
                <span className="font-mono text-[9px] text-muted-foreground/70">no expenditure restriction</span>
              )}
            </div>
            <div className="mt-3 flex items-center justify-between border-t border-border pt-2 font-mono text-[10px] text-muted-foreground/70">
              <span>{f.fund_source}</span>
              <span>{f.geography_scope || "—"}</span>
            </div>
          </div>
        ))}
      </div>

      <Panel testId="panel-funds-table" kicker="Chart of funds" title="All funds">
        <Table testId="funds-table"
               head={<><Th>Code</Th><Th>Name</Th><Th>Type</Th><Th>Source</Th><Th>Balance</Th><Th>Effective from</Th></>}>
          {funds.length === 0 && <Empty colSpan={6} />}
          {funds.map((f) => (
            <tr key={refOf(f)} className="row-hover cursor-pointer" onClick={rowClick(() => detail.show(f, refOf(f)))} data-testid={`fund-row-${f.code}`}>
              <Td className="font-mono text-primary">{f.code}</Td>
              <Td>{f.name}</Td>
              <Td><Chip className={TYPE_STYLE[f.fund_type]}>{f.fund_type}</Chip></Td>
              <Td className="font-mono text-[11px]">{f.fund_source}</Td>
              <Td className="font-mono">{money(f.balance, f.currency)}</Td>
              <Td className="font-mono text-[11px]">{f.effective_from ? shortDate(f.effective_from) : "—"}</Td>
            </tr>
          ))}
        </Table>
      </Panel>

      {detail.sheet}
    </div>
  );
}

const F = ({ label, children, full }) => (
  <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}>
    <Label className="text-[11px] text-muted-foreground">{label}</Label>
    {children}
  </div>
);
